'use client';

/**
 * Ripple Effect Hook
 *
 * A custom hook that provides material-design-like ripple effects for UI elements.
 */

import * as React from 'react';
import { ButtonVariant } from '../types';
/**
 * A CSS Module, not a global stylesheet.
 *
 * Next only permits global CSS imports from _app, so a shared component that pulled in a plain
 * .css file could not be rendered by any app but the one whose _app happened to import it -
 * dealersign failed to compile the moment it rendered a Button. Module scoping makes the component
 * self-contained.
 *
 * Note the class names below are the hashed values from this import, never string literals: the
 * DOM queries here must match what the bundler actually emitted.
 */
import rippleStyles from './css/ripple.module.css';

interface RippleProps {
  variant?: ButtonVariant;
  className?: string;
}

export const useRipple = ({ variant, className }: RippleProps = {}) => {
  const hasRippleStyles = React.useMemo(() => {
    try {
      const element = document.createElement('span');
      element.className = rippleStyles.ripple as string;
      document.body.appendChild(element);
      const styles = window.getComputedStyle(element);
      const hasStyles = styles.position === 'absolute';
      document.body.removeChild(element);
      return hasStyles;
    } catch (e) {
      return false;
    }
  }, []);

  const createRipple = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!hasRippleStyles) return;

      const element = event.currentTarget;
      const rect = element.getBoundingClientRect();
      const diameter = Math.sqrt(rect.width ** 2 + rect.height ** 2) * 2;
      const isDarkRipple = variant === 'outlined' || variant === 'ghost';

      // Remove existing ripple
      element.getElementsByClassName(rippleStyles.ripple as string)[0]?.remove();

      // Create and position new ripple
      const circle = document.createElement('span');
      Object.assign(circle.style, {
        width: `${diameter}px`,
        height: `${diameter}px`,
        left: `${event.clientX - rect.left - diameter / 2}px`,
        top: `${event.clientY - rect.top - diameter / 2}px`,
      });

      // Add classes
      circle.classList.add(
        rippleStyles.ripple as string,
        (isDarkRipple ? rippleStyles['ripple-dark'] : rippleStyles['ripple-light']) as string
      );

      element.appendChild(circle);
    },
    [hasRippleStyles, variant]
  );

  const removeRipple = React.useCallback(() => {
    if (!hasRippleStyles) return;

    const ripples = document.getElementsByClassName(rippleStyles.ripple as string);
    if (ripples.length) {
      const ripple = ripples[0];
      ripple.classList.add(rippleStyles['ripple-release'] as string);
      setTimeout(() => ripple.remove(), 700);
    }
  }, [hasRippleStyles]);

  return {
    rippleHandlers: {
      onMouseDown: createRipple,
      onMouseUp: removeRipple,
      onMouseLeave: removeRipple,
    },
    rippleClassName: 'relative overflow-hidden',
  };
};
